import type { UseMutationResult } from "@tanstack/react-query"

import { useSolarLightReachCommand } from "./solar-lightreach-cache"
import type { SolarLightReachDto } from "../dto/solar-lightreach.dto"
import { solarProposalService } from "../services/solar-proposal.service"

export type SolarLightReachContractCommand =
  | { kind: `refresh` }
  | { kind: `retrySupplemental`; attemptId: string }
  | { kind: `send` }

const run = (
  proposalId: string,
  command: SolarLightReachContractCommand,
): Promise<SolarLightReachDto> => {
  switch (command.kind) {
    case `send`:
      return solarProposalService.sendLightReachContract(proposalId)
    case `refresh`:
      return solarProposalService.refreshLightReachContract(proposalId)
    case `retrySupplemental`:
      return solarProposalService.retryLightReachSupplemental(
        proposalId,
        command.attemptId,
      )
  }
}

/**
 * Send, refresh, or retry the supplemental for LightReach's contract. Each
 * answers with the LightReach read; the contract drawer, the header and the
 * lock read it too. A failure may still have reached LightReach, so it reads
 * everything again.
 */
export default function useSolarLightReachContract(): UseMutationResult<
  SolarLightReachDto,
  unknown,
  { proposalId: string; command: SolarLightReachContractCommand }
> {
  return useSolarLightReachCommand(
    ({ proposalId, command }) => run(proposalId, command),
    `contract`,
  )
}

/** Void an unsigned LightReach contract; it stays as history. */
export function useVoidSolarLightReachContract(): UseMutationResult<
  SolarLightReachDto,
  unknown,
  { proposalId: string }
> {
  return useSolarLightReachCommand(
    ({ proposalId }) => solarProposalService.voidLightReachContract(proposalId),
    `contract`,
  )
}
